import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import FAQPage from './pages/FAQPage/FAQPage';
import ContactPage from './pages/ContactPage/ContactPage';
import BlogListingPage from './pages/BlogListingPage/BlogListingPage';
import TechnicalInterviewPlatformsPost from './pages/BlogPage/BlogPage';
import CoderPadAlternativesPost from './pages/BlogPage/CoderPadAlternativesPost';
import BestCodingInterviewPlatformForStartupsPost from './pages/BlogPage/BestCodingInterviewPlatformForStartupsPost';
import { FREE_MODE_ENABLED } from './constants/freeMode';

const LandingPage = lazy(() => import('./pages/LandingPage/LandingPage'));
const CodePad = lazy(() => import('./pages/CodePad/CodePad'));
const SignInPage = lazy(() => import('./pages/SignInPage/SignInPage'));
const SignUpPage = lazy(() => import('./pages/SignUpPage/SignUpPage'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const JoinPad = lazy(() => import('./pages/JoinPad/JoinPad'));
const ExitInterview = lazy(() => import('./pages/ExitInterview/ExitInterview'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage/TermsPage'));

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <Suspense fallback={
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100vh',
            flexDirection: 'column',
            gap: '1rem',
            fontFamily: 'Inter, sans-serif',
            color: '#6b7280'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <div>Loading...</div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        }>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/join" element={<JoinPad />} />
            <Route path="/pad" element={<CodePad />} />
            <Route path="/signin" element={
              <ProtectedRoute requireAuth={false}>
                <SignInPage />
              </ProtectedRoute>
            } />
            <Route path="/signup" element={
              <ProtectedRoute requireAuth={false}>
                <SignUpPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/exit-interview" element={<ExitInterview />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/faq" element={FREE_MODE_ENABLED ? <Navigate to="/" replace /> : <FAQPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/blog" element={<BlogListingPage />} />
            <Route path="/blog/technical-interview-platforms-comparison" element={<TechnicalInterviewPlatformsPost />} />
            <Route path="/blog/7-best-alternatives-to-coderpad" element={<CoderPadAlternativesPost />} />
            <Route path="/blog/best-coding-interview-platform-for-startups" element={<BestCodingInterviewPlatformForStartupsPost />} />
          </Routes>
        </Suspense>
      </Router>
      <Analytics />
    </AuthProvider>
  );
};

export default App;
